/**
 * useCourses Hook
 *
 * Fetches list of academy courses with user progress using URQL.
 */

import { useQuery } from "urql";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AcademyCoursesDocument = `
  query AcademyCourses {
    academyCourses {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      exam {
        id
        name
        questionsCount
      }
      userProgress {
        visited
        visitCount
        examStarted
        examPassed
        examPassedAt
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface CourseExamSummary {
  id: string;
  name: string;
  questionsCount: number;
}

export interface CourseUserProgress {
  visited: boolean;
  visitCount: number;
  examStarted: boolean;
  examPassed: boolean;
  examPassedAt: string | null;
}

export interface AcademyCourse {
  id: string;
  externalId: string | null;
  title: string;
  description: string | null;
  externalUrl: string | null;
  imageUrl: string | null;
  sortOrder: number;
  exam: CourseExamSummary | null;
  userProgress: CourseUserProgress | null;
}

interface UseCoursesOptions {
  pause?: boolean;
}

interface UseCoursesResult {
  courses: AcademyCourse[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch list of academy courses with user progress.
 */
export function useCourses(options: UseCoursesOptions = {}): UseCoursesResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ academyCourses: AcademyCourse[] }>({
    query: AcademyCoursesDocument,
    pause,
  });

  return {
    courses: result.data?.academyCourses ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
